/**
 * @fileoverview Drag, drop, and reschedule handlers for CustomCalendar.
 * @module components/CustomCalendar/useCustomCalendarDrag
 */
import { useState } from "react";
import { hasCompanyUserConflict as detectCompanyUserConflict } from "../../utils/calendar/calendarAppointmentUtils.js";
import { isValidDropTarget as checkValidDropTarget } from "./customCalendarUtils.js";

export function useCustomCalendarDrag({
  appointments,
  allAppointments,
  currentAppointment,
  eventData,
  breakTimes,
  onAppointmentReschedule,
}) {
  const [draggedAppointment, setDraggedAppointment] = useState(null);
  const [dragOverTimeSlot, setDragOverTimeSlot] = useState(null);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [rescheduleData, setRescheduleData] = useState(null);
  const [isRescheduling, setIsRescheduling] = useState(false);

  const isValidDropTarget = (date, timeSlot) =>
    checkValidDropTarget({
      date,
      timeSlot,
      breakTimes,
      eventData,
      appointments,
    });

  const hasCompanyUserConflict = (date, timeSlot) =>
    detectCompanyUserConflict({
      date,
      timeSlot,
      appointmentToCheck: draggedAppointment || currentAppointment,
      allAppointments,
      appointments,
    });

  const handleDragStart = (e, appointment) => {
    setDraggedAppointment(appointment);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.outerHTML);

    e.target.style.opacity = "0.5";
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDraggedAppointment(null);
    setDragOverTimeSlot(null);
  };

  const handleDragOver = (e, date, timeSlot) => {
    e.preventDefault();

    const isValid = isValidDropTarget(date, timeSlot);
    const hasConflict = hasCompanyUserConflict(date, timeSlot);

    if (isValid && !hasConflict) {
      e.dataTransfer.dropEffect = "move";
      setDragOverTimeSlot({ date, timeSlot });
    } else {
      e.dataTransfer.dropEffect = "none";
      setDragOverTimeSlot(null);
    }
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverTimeSlot(null);
    }
  };

  const handleDrop = (e, date, timeSlot) => {
    e.preventDefault();

    if (!draggedAppointment) {
      return;
    }

    if (!isValidDropTarget(date, timeSlot)) {
      setDragOverTimeSlot(null);
      return;
    }

    if (hasCompanyUserConflict(date, timeSlot)) {
      setDragOverTimeSlot(null);
      return;
    }

    const originalDate = new Date(draggedAppointment.fecha + "T00:00:00");
    const [originalHour, originalMinute] = draggedAppointment.horaInicio
      .split(":")
      .map(Number);

    if (
      originalDate.toDateString() === date.toDateString() &&
      originalHour === timeSlot.hour &&
      originalMinute === timeSlot.minute
    ) {
      setDragOverTimeSlot(null);
      return;
    }

    const newTime = `${timeSlot.hour
      .toString()
      .padStart(2, "0")}:${timeSlot.minute.toString().padStart(2, "0")}`;
    setRescheduleData({
      appointment: draggedAppointment,
      newDate: date,
      newTime: newTime,
    });

    setIsRescheduleModalOpen(true);
    setDragOverTimeSlot(null);
  };

  const handleRescheduleConfirm = async (rescheduleData) => {
    if (onAppointmentReschedule && rescheduleData) {
      setIsRescheduling(true);
      try {
        await onAppointmentReschedule(rescheduleData);
      } finally {
        setIsRescheduling(false);
      }
    }
    setIsRescheduleModalOpen(false);
    setRescheduleData(null);
  };

  const handleRescheduleCancel = () => {
    setIsRescheduleModalOpen(false);
    setRescheduleData(null);
  };

  return {
    draggedAppointment,
    dragOverTimeSlot,
    isRescheduleModalOpen,
    rescheduleData,
    isRescheduling,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleRescheduleConfirm,
    handleRescheduleCancel,
  };
}
